import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import useData from "../../../hooks/useData";

const escapeCsvCell = (value) => {
  const text = String(value ?? "");
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const ClientPortalInventoryReportsModule = () => {
  const { executeAction } = useData();

  const [reportType, setReportType] = useState("consumption");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [locationId, setLocationId] = useState("");
  const [rep, setRep] = useState("");
  const [inventoryCode, setInventoryCode] = useState("");

  const [locationOptions, setLocationOptions] = useState([]);
  const [repOptions, setRepOptions] = useState([]);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [hasRun, setHasRun] = useState(false);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  const hasDateRange = !!dateFrom && !!dateTo;

  const getReportLabel = () => {
    if (reportType === "lotExpiry") {
      return "Lot and Expiry Report";
    }
    if (reportType === "hospitalLocationSummary") {
      return "Hospital Location Summary";
    }
    if (reportType === "hospitalLocationDetail") {
      return "Hospital Location Detail";
    }
    return "Consumption Report";
  };

  const reportLabel = getReportLabel();

  const selectedColumnCount =
    reportType === "lotExpiry"
      ? 15
      : reportType === "hospitalLocationSummary"
        ? 7
        : reportType === "hospitalLocationDetail"
          ? 8
          : 10;

  const loadFilters = async () => {
    setIsLoadingFilters(true);

    const result = await executeAction({
      entityName: "Client",
      action: "getMyPortalInventoryReportFilters",
    });

    if (result?.isSuccessful) {
      setLocationOptions(
        Array.isArray(result?.data?.locations) ? result.data.locations : [],
      );
      setRepOptions(Array.isArray(result?.data?.reps) ? result.data.reps : []);
      setStatusMessage("");
    } else {
      setLocationOptions([]);
      setRepOptions([]);
      setStatusMessage(result?.message || "Unable to load report filters.");
    }

    setIsLoadingFilters(false);
  };

  const fetchReport = async ({
    nextPage = page,
    nextRowsPerPage = rowsPerPage,
    returnAll = false,
  } = {}) => {
    if (!hasDateRange) {
      setStatusMessage("Date From and Date To are required.");
      return null;
    }

    setIsLoadingReport(true);

    const result = await executeAction({
      entityName: "Client",
      action: "getMyPortalInventoryReport",
      data: {
        reportType,
        filter: {
          dateFrom,
          dateTo,
          locationId,
          rep,
          inventoryCode,
        },
        pageNumber: nextPage + 1,
        recordsPerPage: nextRowsPerPage,
        returnAll,
      },
    });

    if (result?.isSuccessful) {
      const responseRows = Array.isArray(result?.data?.rows)
        ? result.data.rows
        : [];
      if (!returnAll) {
        setRows(responseRows);
        setTotal(Number(result?.data?.total || 0));
      }
      setStatusMessage("");
    } else {
      if (!returnAll) {
        setRows([]);
        setTotal(0);
      }
      setStatusMessage(result?.message || "Unable to run report.");
    }

    setIsLoadingReport(false);
    return result;
  };

  useEffect(() => {
    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasRun) return;
    fetchReport({ nextPage: page, nextRowsPerPage: rowsPerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const runReport = async () => {
    if (!hasDateRange) {
      setStatusMessage("Date From and Date To are required.");
      return;
    }

    setHasRun(true);
    setPage(0);
    await fetchReport({ nextPage: 0, nextRowsPerPage: rowsPerPage });
  };

  const exportReportCsv = async () => {
    const result = await fetchReport({ returnAll: true });
    if (!result?.isSuccessful) return;

    const exportRows = Array.isArray(result?.data?.rows)
      ? result.data.rows
      : [];
    if (exportRows.length === 0) return;

    const headers =
      reportType === "lotExpiry"
        ? [
            "Reference Number",
            "Hospital Code",
            "Location Name",
            "Street Address",
            "City",
            "Province",
            "Postal",
            "Rep Name",
            "Delivery Date",
            "Product Code",
            "SKU Name",
            "Inventory Code",
            "Lot Number",
            "Expiry Date",
            "Delivered Units",
          ]
        : reportType === "hospitalLocationSummary"
          ? [
              "Rep Name",
              "Hospital Code",
              "Hospital Name",
              "Product Code",
              "SKU Name",
              "Hospital Location",
              "Delivered Units",
            ]
          : reportType === "hospitalLocationDetail"
            ? [
                "Rep Name",
                "Hospital Code",
                "Hospital Name",
                "Delivery Date",
                "Product Code",
                "SKU Name",
                "Hospital Location",
                "Delivered Units",
              ]
            : [
                "Hospital Code",
                "Location Name",
                "Street Address",
                "City",
                "Province",
                "Postal",
                "Rep Name",
                "Product Code",
                "SKU Name",
                "Delivered Units",
              ];

    const body = exportRows.map((row) => {
      if (reportType === "lotExpiry") {
        return [
          row.referenceNumber,
          row.hospitalCode,
          row.locationName,
          row.streetAddress,
          row.city,
          row.province,
          row.postal,
          row.repName,
          row.deliveryDate,
          row.productCode,
          row.skuName,
          row.inventoryCode,
          row.lotNumber,
          row.expiryDate,
          row.deliveredUnits,
        ]
          .map(escapeCsvCell)
          .join(",");
      }

      if (reportType === "hospitalLocationSummary") {
        return [
          row.repName,
          row.hospitalCode,
          row.hospitalName,
          row.productCode,
          row.skuName,
          row.hospitalLocationName,
          row.deliveredUnits,
        ]
          .map(escapeCsvCell)
          .join(",");
      }

      if (reportType === "hospitalLocationDetail") {
        return [
          row.repName,
          row.hospitalCode,
          row.hospitalName,
          row.deliveryDate || "-",
          row.productCode,
          row.skuName,
          row.hospitalLocationName,
          row.deliveredUnits,
        ]
          .map(escapeCsvCell)
          .join(",");
      }

      return [
        row.hospitalCode,
        row.locationName,
        row.streetAddress,
        row.city,
        row.province,
        row.postal,
        row.repName,
        row.productCode,
        row.skuName,
        row.deliveredUnits,
      ]
        .map(escapeCsvCell)
        .join(",");
    });

    const csv = [headers.join(","), ...body].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute(
      "download",
      `${
        reportType === "lotExpiry"
          ? "lot-and-expiry"
          : reportType === "hospitalLocationSummary"
            ? "hospital-location-summary"
            : reportType === "hospitalLocationDetail"
              ? "hospital-location-detail"
              : "consumption"
      }-report-${dateFrom}-to-${dateTo}.csv`,
    );

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const reportRowsMessage = useMemo(() => {
    if (!hasDateRange) {
      return "Date From and Date To are required to run this report.";
    }

    if (!hasRun) {
      return `Set filters and click Run ${reportLabel}.`;
    }

    if (rows.length === 0) {
      return "No report data matches the selected filters.";
    }

    return "";
  }, [hasDateRange, hasRun, rows.length, reportLabel]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="h6">Inventory Reports</Typography>

      <Card sx={{ p: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, mb: 1 }}
        >
          Report Filters
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          <FormControl
            size="small"
            sx={{ minWidth: 240 }}
          >
            <InputLabel id="portal-report-type-label">Report Type</InputLabel>
            <Select
              labelId="portal-report-type-label"
              value={reportType}
              label="Report Type"
              onChange={(event) => setReportType(event.target.value)}
            >
              <MenuItem value="consumption">Consumption Report</MenuItem>
              <MenuItem value="lotExpiry">Lot and Expiry Report</MenuItem>
              <MenuItem value="hospitalLocationSummary">
                Hospital Location Summary
              </MenuItem>
              <MenuItem value="hospitalLocationDetail">
                Hospital Location Detail
              </MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            type="date"
            label="Date From"
            value={dateFrom}
            onChange={(event) => setDateFrom(event.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180 }}
            required
          />

          <TextField
            size="small"
            type="date"
            label="Date To"
            value={dateTo}
            onChange={(event) => setDateTo(event.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180 }}
            required
          />

          <FormControl
            size="small"
            sx={{ minWidth: 260 }}
          >
            <InputLabel id="portal-location-filter-label">Location</InputLabel>
            <Select
              labelId="portal-location-filter-label"
              value={locationId}
              label="Location"
              onChange={(event) => setLocationId(event.target.value)}
            >
              <MenuItem value="">All Locations</MenuItem>
              {locationOptions.map((option) => (
                <MenuItem
                  key={option.id}
                  value={option.id}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {reportType === "lotExpiry" && (
            <TextField
              size="small"
              label="Inventory Code"
              value={inventoryCode}
              onChange={(event) => setInventoryCode(event.target.value)}
              sx={{ minWidth: 220 }}
            />
          )}

          <FormControl
            size="small"
            sx={{ minWidth: 220 }}
          >
            <InputLabel id="portal-rep-filter-label">Rep</InputLabel>
            <Select
              labelId="portal-rep-filter-label"
              value={rep}
              label="Rep"
              onChange={(event) => setRep(event.target.value)}
            >
              <MenuItem value="">All Reps</MenuItem>
              {repOptions.map((repName) => (
                <MenuItem
                  key={repName}
                  value={repName}
                >
                  {repName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={runReport}
            disabled={!hasDateRange || isLoadingReport || isLoadingFilters}
          >
            Run {reportLabel}
          </Button>

          <Button
            variant="outlined"
            onClick={exportReportCsv}
            disabled={rows.length === 0 || isLoadingReport}
          >
            Export {reportLabel} CSV
          </Button>
        </Box>
      </Card>

      <Card sx={{ p: 2 }}>
        <Typography
          variant="h6"
          sx={{ mb: 1 }}
        >
          {reportLabel}
        </Typography>
        <Typography
          color="text.secondary"
          sx={{ mb: 2 }}
        >
          {reportType === "lotExpiry"
            ? "Delivered units by inventory code with lot and expiry details."
            : reportType === "hospitalLocationSummary"
              ? "Delivered units summarized by rep, hospital, hospital location, and product."
              : reportType === "hospitalLocationDetail"
                ? "Delivered units detailed by rep, hospital, hospital location, product, and delivery date."
                : "Delivered units summarized at product level by location."}
        </Typography>

        {statusMessage ? (
          <Typography
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            {statusMessage}
          </Typography>
        ) : null}

        {reportRowsMessage ? (
          <Typography color="text.secondary">{reportRowsMessage}</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {reportType === "lotExpiry" ? (
                    <TableCell>Reference Number</TableCell>
                  ) : null}
                  {(reportType === "hospitalLocationSummary" ||
                    reportType === "hospitalLocationDetail") && (
                    <TableCell>Rep Name</TableCell>
                  )}
                  <TableCell>Hospital Code</TableCell>
                  <TableCell>
                    {reportType === "hospitalLocationSummary" ||
                    reportType === "hospitalLocationDetail"
                      ? "Hospital Name"
                      : "Location Name"}
                  </TableCell>
                  {(reportType === "consumption" ||
                    reportType === "lotExpiry") && (
                    <>
                      <TableCell>Street Address</TableCell>
                      <TableCell>City</TableCell>
                      <TableCell>Province</TableCell>
                      <TableCell>Postal</TableCell>
                      <TableCell>Rep Name</TableCell>
                    </>
                  )}
                  {reportType === "lotExpiry" ? (
                    <TableCell>Delivery Date</TableCell>
                  ) : null}
                  {reportType === "hospitalLocationDetail" ? (
                    <TableCell>Delivery Date</TableCell>
                  ) : null}
                  <TableCell>Product Code</TableCell>
                  <TableCell>SKU Name</TableCell>
                  {(reportType === "hospitalLocationSummary" ||
                    reportType === "hospitalLocationDetail") && (
                    <TableCell>Hospital Location</TableCell>
                  )}
                  {reportType === "lotExpiry" ? (
                    <>
                      <TableCell>Inventory Code</TableCell>
                      <TableCell>Lot Number</TableCell>
                      <TableCell>Expiry Date</TableCell>
                    </>
                  ) : null}
                  <TableCell>Delivered Units</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={selectedColumnCount}>
                      No report data matches the selected filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, idx) => (
                    <TableRow
                      key={`${row.hospitalCode}-${row.hospitalName || row.locationName}-${row.hospitalLocationName || ""}-${row.repName}-${row.productCode}-${row.deliveryDate || ""}-${row.referenceNumber || ""}-${idx}`}
                    >
                      {reportType === "lotExpiry" ? (
                        <TableCell>{row.referenceNumber}</TableCell>
                      ) : null}
                      {(reportType === "hospitalLocationSummary" ||
                        reportType === "hospitalLocationDetail") && (
                        <TableCell>{row.repName}</TableCell>
                      )}
                      <TableCell>{row.hospitalCode}</TableCell>
                      <TableCell>
                        {row.hospitalName || row.locationName}
                      </TableCell>
                      {(reportType === "consumption" ||
                        reportType === "lotExpiry") && (
                        <>
                          <TableCell>{row.streetAddress}</TableCell>
                          <TableCell>{row.city}</TableCell>
                          <TableCell>{row.province}</TableCell>
                          <TableCell>{row.postal}</TableCell>
                          <TableCell>{row.repName}</TableCell>
                        </>
                      )}
                      {reportType === "lotExpiry" ? (
                        <TableCell>{row.deliveryDate || "-"}</TableCell>
                      ) : null}
                      {reportType === "hospitalLocationDetail" ? (
                        <TableCell>{row.deliveryDate || "-"}</TableCell>
                      ) : null}
                      <TableCell>{row.productCode}</TableCell>
                      <TableCell>{row.skuName}</TableCell>
                      {(reportType === "hospitalLocationSummary" ||
                        reportType === "hospitalLocationDetail") && (
                        <TableCell>{row.hospitalLocationName}</TableCell>
                      )}
                      {reportType === "lotExpiry" ? (
                        <>
                          <TableCell>{row.inventoryCode}</TableCell>
                          <TableCell>{row.lotNumber}</TableCell>
                          <TableCell>{row.expiryDate}</TableCell>
                        </>
                      ) : null}
                      <TableCell>{row.deliveredUnits}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_event, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                const nextValue = Number(event.target.value || 25);
                setRowsPerPage(nextValue);
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </TableContainer>
        )}
      </Card>
    </Box>
  );
};

export default ClientPortalInventoryReportsModule;
